import { useAuth, useLogout } from "@/shared/hooks/useAuth";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styles from "./UserProfile.module.css";

interface UserProfileProps {
  isCollapsed?: boolean;
}

const LogoutIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
    />
  </svg>
);

const LoginIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
    />
  </svg>
);

export const UserProfile: React.FC<UserProfileProps> = ({
  isCollapsed = false,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, isAuthenticated } = useAuth();
  const logoutMutation = useLogout();

  const displayName = user?.email?.split("@")[0] || "Пользователь";

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogoutClick = async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (error) {
      console.error("Logout error:", error);
    }
    setIsDropdownOpen(false);
  };

  if (!isAuthenticated) {
    return (
      <div
        className={`${styles.container} ${
          isCollapsed ? styles.containerCollapsed : styles.containerExpanded
        }`}
      >
        {isCollapsed ? (
          <div className={styles.loginContainer}>
            <Link to="/login" className={styles.loginButton} title="Войти">
              <LoginIcon className={styles.loginIcon} />
            </Link>
          </div>
        ) : (
          <Link to="/login" className={styles.loginButtonFull}>
            <LoginIcon
              className={`${styles.loginIcon} ${styles.loginIconMargin}`}
            />
            Войти
          </Link>
        )}
      </div>
    );
  }

  return (
    <div
      className={`${styles.container} ${
        isCollapsed ? styles.containerCollapsed : styles.containerExpanded
      } ${styles.containerRelative}`}
      ref={dropdownRef}
    >
      {isCollapsed ? (
        <div className={styles.avatarButton}>
          <button
            onClick={toggleDropdown}
            className={styles.avatarButtonElement}
            title={displayName}
          >
            <span className={styles.avatarInitial}>
              {displayName.charAt(0).toUpperCase()}
            </span>
          </button>

          {isDropdownOpen && (
            <div className={styles.dropdown}>
              <div className={styles.dropdownUser}>
                <div className={styles.dropdownAvatar}>
                  <span className={styles.avatarInitial}>
                    {displayName.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className={styles.dropdownUserInfo}>
                  <p className={styles.dropdownUserName}>{displayName}</p>
                  <p className={styles.dropdownUserEmail}>{user?.email}</p>
                </div>
              </div>
              <div className={styles.dropdownActions}>
                <Link
                  to="/profile"
                  className={styles.dropdownLink}
                  onClick={() => setIsDropdownOpen(false)}
                >
                  <svg
                    className={styles.dropdownLinkIcon}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  Профиль
                </Link>
                <button
                  onClick={handleLogoutClick}
                  disabled={logoutMutation.isPending}
                  className={styles.dropdownLink}
                  title="Выйти"
                >
                  <LogoutIcon className={styles.dropdownLinkIcon} />
                  Выйти
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className={styles.profileContent}>
          <Link
            to="/profile"
            className={styles.profileLink}
            onClick={() => setIsDropdownOpen(false)}
          >
            <div className={styles.profileAvatar}>
              <span className={styles.avatarInitial}>
                {displayName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className={styles.profileInfo}>
              <p className={styles.profileName}>{displayName}</p>
              <p className={styles.profileEmail}>{user?.email}</p>
            </div>
            <svg
              className={styles.profileArrow}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </Link>
          <button
            onClick={handleLogoutClick}
            disabled={logoutMutation.isPending}
            className={styles.logoutButton}
            title="Выйти"
          >
            <LogoutIcon className={styles.logoutIcon} />
            Выйти
          </button>
        </div>
      )}
    </div>
  );
};
